import { useQuery } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'

import { type LedgerAccount } from '../components/admin/types'

interface PaginationMetadata {
  total_records: number
  current_page: number
  total_pages: number
  next_page: number | null
  prev_page: number | null
}

interface LedgerAccountsResponse {
  data: LedgerAccount[]
  pagination: PaginationMetadata
}

interface UseGetLedgerAccountsArgs {
  id?: string
  query?: string
  cutoff?: string
  pagination: PaginationState
  sorting: SortingState
}

export function useGetLedgerAccounts ({ id, query, cutoff, pagination, sorting }: UseGetLedgerAccountsArgs) {
  // Use new sort format: sort=-path or sort=path
  const sortParam = sorting[0]
    ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}`
    : undefined

  return useQuery<LedgerAccountsResponse>({
    queryKey: ['ledger-accounts', id, query, cutoff, pagination, sorting],
    queryFn: async () => {
      const params = new URLSearchParams({
        'page[number]': pagination.pageIndex.toString(),
        'page[size]': pagination.pageSize.toString(),
      })
      if (sortParam) { params.set('sort', sortParam) }
      if (id) { params.set('id', id) }
      if (query) { params.set('query', query) }
      if (cutoff) { params.set('cutoff', cutoff) }

      const response = await fetchWithHeaders(`/api/web/admin/ledger_accounts?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch ledger accounts')
      }
      return response.json()
    },
  })
}
